import React from 'react';
import {
  withStyles,
  WithStyles,
  createStyles,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  Box,
  Button,
  Grid,
  Tooltip,
} from '@material-ui/core';
import ReplyIcon from '@material-ui/icons/Reply';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import MoreHorizSharpIcon from '@material-ui/icons/MoreHorizSharp';
import LanguageOutlinedIcon from '@material-ui/icons/LanguageOutlined';
import { ImageOutlined } from '@material-ui/icons';

// Read-only port of whatsapp-console-frontend
// `src/pages/FbAdvertisment/NewAdView.js` (single-format image/video ads).
// Inline copy editing, the CTA menu and the carousel format are left out.

export type AdViewPayload = {
  adCaption: string;
  adHeadline: string;
  adHeadlineDescription: string;
  mediaType: 'IMAGE' | 'VIDEO';
  imageFile: { publicUrl: string } | null;
  videoFile: { publicUrl: string } | null;
  thumbnailUrl: string | null;
  ctaButton: string;
  adType: { value: string };
  pageDetails: { picture: string; pageName: string; whatsappNumber: string };
  previewLink: string | null;
};

const CTA_LABELS: Record<string, string> = {
  APPLY_NOW: 'Apply now',
  BOOK_NOW: 'Book now',
  CONTACT_US: 'Contact us',
  DOWNLOAD: 'Download',
  LEARN_MORE: 'Learn more',
  REQUEST_TIME: 'Request time',
  SEE_MENU: 'See menu',
  SHOP_NOW: 'Shop now',
  SIGN_UP: 'Sign up',
  WATCH_MORE: 'Watch more',
  DONATE_NOW: 'Donate now',
};

const WEBSITE_TYPES = ['website_traffic', 'website_to_whatsapp'];

const CARD_SHADOW =
  '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)';

const isValidUrl = (url: string | null): boolean => {
  if (!url) {
    return false;
  }
  const trimmed = url.trim();
  return (
    trimmed !== '' &&
    (trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('/') ||
      trimmed.startsWith('data:'))
  );
};

const NewAdView = (props: Props): React.ReactElement => {
  const {
    classes,
    payload,
    websiteUrl,
    isFromFlowKeyword,
    hideInAppBrowserNote,
    hidePageHeader,
    hideDestination,
    flat,
    footerBg,
    captionClamp,
    mediaMaxHeight,
  } = props;
  const {
    pageDetails,
    adCaption,
    adHeadline,
    adHeadlineDescription,
    mediaType,
    imageFile,
    videoFile,
    thumbnailUrl,
    ctaButton,
    adType,
  } = payload;
  const { picture, pageName, whatsappNumber } = pageDetails;

  const caption = adCaption || ' Enter Caption';
  const headline = adHeadline || `Contact ${pageName}`;

  const captionRef = React.useRef<HTMLElement>(null);
  const [captionExpanded, setCaptionExpanded] = React.useState(false);
  const [captionOverflows, setCaptionOverflows] = React.useState(false);

  React.useEffect(() => {
    const el = captionRef.current;
    if (!el || !captionClamp || captionExpanded) {
      return undefined;
    }
    const measure = () =>
      setCaptionOverflows(el.scrollHeight - el.clientHeight > 1);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [caption, captionClamp, captionExpanded]);

  const clampStyle: React.CSSProperties | null =
    captionClamp && !captionExpanded
      ? {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: captionClamp,
          overflow: 'hidden',
        }
      : null;

  const imageUrl = imageFile ? imageFile.publicUrl : null;
  const videoUrl = videoFile ? videoFile.publicUrl : null;
  const hasValidImage = Boolean(imageFile) && isValidUrl(imageUrl);
  const hasValidVideo = Boolean(videoFile) && isValidUrl(videoUrl);

  const resolvedCta =
    CTA_LABELS[ctaButton] ||
    (ctaButton
      ? ctaButton
          .toString()
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/^\w|\s\w/g, (m) => m.toUpperCase())
      : 'Visit website');

  const renderCardBlock = () => {
    const mediaStyle: React.CSSProperties | undefined = mediaMaxHeight
      ? {
          width: '100%',
          height: mediaMaxHeight,
          maxHeight: mediaMaxHeight,
          objectFit: 'contain',
          position: 'relative',
          display: 'block',
        }
      : undefined;
    const backdropUrl = mediaType === 'VIDEO' ? thumbnailUrl || imageUrl : imageUrl;

    const withMediaWell = (node: React.ReactNode) =>
      !mediaStyle ? (
        node
      ) : (
        <div className={classes.mediaWell} style={{ height: mediaMaxHeight }}>
          {isValidUrl(backdropUrl) ? (
            <div
              aria-hidden="true"
              className={classes.mediaWellBlur}
              style={{ backgroundImage: `url("${backdropUrl}")` }}
            />
          ) : null}
          {node}
        </div>
      );

    return (
      <Box style={{ width: '100%', minWidth: 0 }}>
        {hasValidImage || hasValidVideo ? (
          withMediaWell(
            mediaType === 'VIDEO' && hasValidVideo ? (
              <CardMedia
                className={classes.media}
                style={mediaStyle}
                src={videoUrl || undefined}
                title="Download media"
                component="video"
                controls
              />
            ) : hasValidImage ? (
              <CardMedia
                className={classes.media}
                style={mediaStyle}
                src={imageUrl || undefined}
                alt="media"
                title="Download media"
                component="img"
              />
            ) : null,
          )
        ) : (
          <Box
            height="250px"
            width="100%"
            bgcolor="#fafafa"
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <Grid style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <ImageOutlined
                style={{ width: '100px', height: '100px', color: 'rgb(208 212 208)' }}
              />
            </Grid>
          </Box>
        )}

        <Box
          bgcolor={footerBg || '#fafafa'}
          style={{ display: 'flex', paddingTop: hideDestination ? 10 : 0 }}
        >
          <Box
            style={{
              display: 'flex',
              width: '60%',
              minWidth: 0,
              justifyContent: 'space-between',
            }}
          >
            <Box style={{ width: '100%', minWidth: 0 }}>
              {hideDestination ? null : (
                <Typography
                  variant="h5"
                  style={{ margin: '10px', marginBottom: '0px', fontSize: '12px' }}
                  gutterBottom
                >
                  {WEBSITE_TYPES.includes(adType.value)
                    ? websiteUrl
                      ? websiteUrl.replace(/^https?:\/\//, '')
                      : 'WEBSITE'
                    : adType.value === 'lead_form'
                    ? 'LEAD FORM'
                    : 'WHATSAPP'}
                </Typography>
              )}
              <Typography
                variant="h5"
                style={{
                  marginLeft: '10px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: headline ? 'black' : 'rgb(211 214 211)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {headline}
              </Typography>
              {adHeadlineDescription && (
                <Typography
                  variant="body2"
                  style={{
                    marginLeft: '10px',
                    fontSize: '12px',
                    color: 'rgb(120,120,120)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {adHeadlineDescription}
                </Typography>
              )}
              <Box marginBottom={0.5} />
            </Box>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', width: '40%' }}>
            {WEBSITE_TYPES.includes(adType.value) ? (
              <Tooltip title={websiteUrl || 'No URL added'} arrow>
                <span style={{ display: 'inline-flex' }}>
                  {websiteUrl ? (
                    <Button
                      variant="outlined"
                      style={{
                        color: '#darkgreen',
                        borderColor: 'darkgreen',
                        cursor: 'pointer',
                        marginLeft: '30px',
                        whiteSpace: 'nowrap',
                      }}
                      component="a"
                      href={websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                    >
                      {resolvedCta}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      style={{
                        color: 'rgba(0, 0, 0, 0.26)',
                        borderColor: 'rgba(0, 0, 0, 0.26)',
                        cursor: 'not-allowed',
                        marginLeft: '30px',
                        whiteSpace: 'nowrap',
                      }}
                      size="small"
                      disabled
                    >
                      {resolvedCta}
                    </Button>
                  )}
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                style={{ color: '#darkgreen', borderColor: 'darkgreen' }}
                size="small"
                startIcon={<WhatsAppIcon />}
              >
                Whatsapp
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // WhatsApp Status ads render as a full-screen vertical Status/Story surface.
  if (adType.value === 'whatsapp_status') {
    return (
      <Box className={classes.statusCard}>
        {hasValidImage || hasValidVideo ? (
          mediaType === 'VIDEO' && hasValidVideo ? (
            <video
              src={videoUrl || undefined}
              poster={thumbnailUrl || undefined}
              className={classes.statusMedia}
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img src={imageUrl || undefined} alt="media" className={classes.statusMedia} />
          )
        ) : (
          <Box
            className={classes.statusMedia}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#111',
            }}
          >
            <ImageOutlined
              style={{ width: '80px', height: '80px', color: 'rgba(255,255,255,0.3)' }}
            />
          </Box>
        )}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '35%',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '30%',
            background: 'linear-gradient(0deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 100%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            right: '8px',
            display: 'flex',
            gap: '4px',
          }}
        >
          {[0, 1, 2].map((segment) => (
            <Box
              key={segment}
              style={{
                flex: 1,
                height: '3px',
                borderRadius: '3px',
                backgroundColor: segment === 0 ? '#fff' : 'rgba(255,255,255,0.35)',
              }}
            />
          ))}
        </Box>
        <Box
          style={{
            position: 'absolute',
            top: '16px',
            left: '8px',
            right: '8px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={picture || undefined}
            style={{
              width: '28px',
              height: '28px',
              marginRight: '8px',
              border: '1px solid rgba(255,255,255,0.6)',
            }}
          />
          <Box>
            <Typography style={{ color: '#fff', fontSize: '12px', fontWeight: 600, lineHeight: 1.2 }}>
              {pageName || 'Page Name'}
            </Typography>
            <Typography style={{ color: 'rgba(255,255,255,0.75)', fontSize: '10px', lineHeight: 1.2 }}>
              Sponsored
            </Typography>
          </Box>
        </Box>
        {adCaption && (
          <Typography
            style={{
              position: 'absolute',
              bottom: '56px',
              left: '12px',
              right: '12px',
              color: '#fff',
              fontSize: '12px',
              whiteSpace: 'pre-line',
              wordBreak: 'break-word',
              textShadow: '0px 1px 2px rgba(0,0,0,0.6)',
            }}
          >
            {caption}
          </Typography>
        )}
        <Box
          style={{
            position: 'absolute',
            bottom: '14px',
            left: '12px',
            right: '12px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<WhatsAppIcon style={{ fontSize: '16px' }} />}
            style={{
              backgroundColor: '#fff',
              color: '#075E54',
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '12px',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            Chat on WhatsApp
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box style={{ width: 'fit-content' }}>
      <div style={{ width: 'fit-content', display: 'inline-block' }}>
        <Card
          className={classes.root}
          style={{
            width: '300px',
            overflow: 'hidden',
            ...(flat ? { boxShadow: 'none', borderRadius: 0 } : {}),
          }}
        >
          {!hidePageHeader ? (
            <Box style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
              <Box style={{ display: 'flex', alignItems: 'center', margin: '15px 15px 0' }}>
                <Box style={{ marginRight: '10px' }}>
                  <Avatar src={picture || undefined} />
                </Box>
                <Box>
                  <Typography variant="h5">{pageName || 'Page Name'}</Typography>
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginRight: '0.2em' }}
                    >
                      sponsored
                    </Typography>
                    <LanguageOutlinedIcon style={{ fontSize: '15px' }} color="action" />
                  </Box>
                </Box>
              </Box>
              {!isFromFlowKeyword && (
                <MoreHorizSharpIcon
                  style={{ margin: '15px 15px 0', fontSize: '18px', display: 'flex' }}
                  color="action"
                />
              )}
            </Box>
          ) : null}

          <Box>
            <CardContent style={{ padding: '16px', paddingBottom: '8px' }}>
              <Typography
                ref={captionRef}
                variant="h6"
                align="left"
                style={{
                  color: adCaption ? 'black' : 'rgb(178 182 178)',
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  ...clampStyle,
                }}
              >
                {caption}
              </Typography>
              {captionClamp && (captionOverflows || captionExpanded) ? (
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCaptionExpanded((open) => !open);
                  }}
                  style={{
                    display: 'inline-block',
                    marginTop: '4px',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#0a474c',
                    cursor: 'pointer',
                  }}
                >
                  {captionExpanded ? 'See less' : 'See more'}
                </Typography>
              ) : null}
            </CardContent>

            {renderCardBlock()}

            {!isFromFlowKeyword && (
              <Box style={{ display: 'flex', width: '100%' }}>
                <Box
                  style={{
                    display: 'flex',
                    width: '100%',
                    margin: '10px',
                    marginTop: '0px',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <ThumbUpIcon
                      style={{
                        margin: '10px',
                        marginBottom: '0px',
                        marginRight: '0.2em',
                        fontSize: '15px',
                      }}
                    />
                    <Typography
                      color="primary"
                      variant="h5"
                      style={{
                        margin: '10px',
                        marginBottom: '0px',
                        marginLeft: '0px',
                        fontSize: '13px',
                      }}
                      gutterBottom
                    >
                      Like
                    </Typography>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <ReplyIcon
                      style={{
                        margin: '10px',
                        marginBottom: '0px',
                        marginRight: '0.2em',
                        fontSize: '15px',
                        transform: 'rotateY(180deg)',
                      }}
                    />
                    <Typography
                      color="primary"
                      variant="h5"
                      style={{
                        margin: '10px',
                        marginBottom: '0px',
                        marginLeft: '0',
                        fontSize: '13px',
                      }}
                      gutterBottom
                    >
                      Share
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Card>
      </div>
      {adType.value === 'website_to_whatsapp' && !hideInAppBrowserNote && (
        <Box style={{ marginTop: '16px', width: '300px' }}>
          <Typography
            variant="body2"
            style={{ fontSize: '12px', color: 'rgb(120,120,120)', marginBottom: '8px', lineHeight: 1.4 }}
          >
            On tap in the Facebook/Instagram mobile app, your website opens with a
            WhatsApp button (mobile only):
          </Typography>
          <Box
            style={{
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundColor: '#fff',
              boxShadow: CARD_SHADOW,
            }}
          >
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <Typography style={{ fontSize: '12px', color: '#666' }}>✕</Typography>
              <Box style={{ flex: 1, textAlign: 'center' }}>
                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#111' }}>
                  {websiteUrl
                    ? websiteUrl.replace(/^https?:\/\//, '').split('/')[0]
                    : 'yourwebsite.com'}
                </Typography>
                <Typography style={{ fontSize: '10px', color: '#999' }}>Facebook</Typography>
              </Box>
              <Typography style={{ fontSize: '12px', color: '#666' }}>⋯</Typography>
            </Box>
            <Box
              style={{
                height: '80px',
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography style={{ fontSize: '11px', color: '#c0c0c0' }}>Your website</Typography>
            </Box>
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                padding: '10px 12px',
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Box
                  style={{
                    position: 'relative',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    backgroundColor: '#e8f5ee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: '#109d59',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {(pageName || 'P').charAt(0).toUpperCase()}
                </Box>
                <Box style={{ minWidth: 0 }}>
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#111',
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {pageName || 'Your Page'}
                  </Typography>
                  {whatsappNumber && (
                    <Typography style={{ fontSize: '11px', color: '#888', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                      {whatsappNumber.startsWith('+') ? whatsappNumber : `+${whatsappNumber}`}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  backgroundColor: '#128C7E',
                  borderRadius: '18px',
                  padding: '6px 12px',
                  flexShrink: 0,
                }}
              >
                <WhatsAppIcon style={{ color: '#fff', fontSize: '15px' }} />
                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>
                  WhatsApp
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface Props extends WithStyles<typeof styles> {
  payload: AdViewPayload;
  websiteUrl: string;
  isFromFlowKeyword?: boolean;
  hideInAppBrowserNote?: boolean;
  hidePageHeader?: boolean;
  hideDestination?: boolean;
  // Drops the card frame when the preview sits inside another card.
  flat?: boolean;
  footerBg?: string;
  captionClamp?: number;
  mediaMaxHeight?: number;
}

const styles = () =>
  createStyles({
    root: {
      height: '100%',
      boxShadow: CARD_SHADOW,
    },
    media: {
      maxHeight: 600,
      objectFit: 'contain',
    },
    mediaWell: {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
    },
    mediaWellBlur: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      filter: 'blur(18px)',
      transform: 'scale(1.2)',
      pointerEvents: 'none',
    },
    statusCard: {
      position: 'relative',
      width: '270px',
      aspectRatio: '9 / 16',
      borderRadius: '12px',
      overflow: 'hidden',
      backgroundColor: '#000',
      boxShadow: CARD_SHADOW,
    },
    statusMedia: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  });

export default withStyles(styles)(NewAdView);
